import { useState } from 'react'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  FileText,
  Wand2,
  Copy,
  RotateCcw,
  AlertCircle,
  Sparkles,
  BookOpen,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { type AgentBuilderState } from '../AgentBuilder'

interface SystemPromptStepProps {
  state: AgentBuilderState
  updateState: (updates: Partial<AgentBuilderState>) => void
  errors: Record<string, string>
}

const PROMPT_TEMPLATES = [
  {
    id: 'content-writer',
    name: 'Content Writer',
    description: 'Optimized for creating written content',
    prompt: `You are a professional content writer assistant. Your role is to help users create high-quality written content including:

- Blog posts and articles
- Marketing copy and advertisements
- Social media content
- Email campaigns
- Product descriptions

Guidelines:
1. Always maintain a consistent tone that matches the brand voice
2. Use clear, engaging language that resonates with the target audience
3. Structure content with proper headings and formatting
4. Include relevant calls-to-action where appropriate
5. Proofread for grammar, spelling, and clarity

When asked to write content, first clarify the target audience, desired tone, and key messages if not provided.`,
  },
  {
    id: 'data-analyst',
    name: 'Data Analyst',
    description: 'Specialized in data analysis and insights',
    prompt: `You are a data analysis assistant specialized in extracting insights from data. Your capabilities include:

- Analyzing datasets and identifying patterns
- Creating summaries and visualizations recommendations
- Performing statistical analysis
- Generating reports and dashboards suggestions
- Answering questions about data

Guidelines:
1. Always clarify the data structure and format being analyzed
2. Provide clear explanations of your analysis methodology
3. Present findings in an organized, easy-to-understand format
4. Highlight key insights and actionable recommendations
5. Note any limitations or caveats in your analysis

When analyzing data, start by understanding the user's goals and what questions they want answered.`,
  },
  {
    id: 'customer-support',
    name: 'Customer Support',
    description: 'Helpful for customer service interactions',
    prompt: `You are a helpful customer support assistant. Your primary goals are to:

- Resolve customer issues efficiently and empathetically
- Provide accurate information about products and services
- Guide users through troubleshooting steps
- Escalate complex issues when necessary
- Maintain a positive and professional tone

Guidelines:
1. Always greet customers warmly and acknowledge their concerns
2. Listen carefully and ask clarifying questions when needed
3. Provide step-by-step solutions when troubleshooting
4. Offer alternatives if the primary solution doesn't work
5. End interactions by confirming the issue is resolved

If you don't have enough information to help, clearly explain what additional details you need.`,
  },
]

const VARIABLE_SUGGESTIONS = [
  { name: '{{user_name}}', description: "Insert the user's name" },
  { name: '{{company_name}}', description: 'Insert the company name' },
  { name: '{{date}}', description: 'Insert current date' },
  { name: '{{context}}', description: 'Insert conversation context' },
]

export function SystemPromptStep({ state, updateState, errors }: SystemPromptStepProps) {
  const [showTemplates, setShowTemplates] = useState(false)
  const [showVariables, setShowVariables] = useState(false)

  const handleTemplateSelect = (prompt: string) => {
    updateState({ systemPrompt: prompt, useTemplate: true })
    setShowTemplates(false)
  }

  const handleGeneratePrompt = () => {
    // Generate a basic prompt based on agent configuration
    const generatedPrompt = `You are ${state.name || 'an AI assistant'}${
      state.description ? `, ${state.description.toLowerCase()}` : ''
    }.

Your communication style is ${state.personality.tone} and ${state.personality.style}.
${state.personality.expertise.length > 0 ? `You have expertise in: ${state.personality.expertise.join(', ')}.` : ''}

Guidelines:
1. Respond in a ${(state.personality.formalityLevel ?? 5) >= 7 ? 'formal' : (state.personality.formalityLevel ?? 5) <= 3 ? 'casual' : 'balanced'} manner
2. Keep responses ${(state.personality.verbosityLevel ?? 5) >= 7 ? 'detailed and comprehensive' : (state.personality.verbosityLevel ?? 5) <= 3 ? 'brief and concise' : 'appropriately detailed'}
3. ${(state.personality.creativityLevel ?? 5) >= 7 ? 'Be creative and imaginative in your responses' : (state.personality.creativityLevel ?? 5) <= 3 ? 'Focus on facts and accuracy' : 'Balance creativity with practicality'}
4. Always be helpful and respectful
5. Ask clarifying questions when needed

Primary language: ${state.personality.language === 'en' ? 'English' : state.personality.language}`

    updateState({ systemPrompt: generatedPrompt, useTemplate: false })
  }

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('system-prompt') as HTMLTextAreaElement
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const newPrompt =
        state.systemPrompt.substring(0, start) +
        variable +
        state.systemPrompt.substring(end)
      updateState({ systemPrompt: newPrompt })
      // Set cursor position after the inserted variable
      setTimeout(() => {
        textarea.setSelectionRange(start + variable.length, start + variable.length)
        textarea.focus()
      }, 0)
    }
  }

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(state.systemPrompt)
  }

  const handleResetPrompt = () => {
    updateState({ systemPrompt: '', useTemplate: false })
  }

  const wordCount = state.systemPrompt.trim().split(/\s+/).filter(Boolean).length
  const charCount = state.systemPrompt.length

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">System Prompt</h2>
        <p className="text-gray-500 dark:text-gray-400">
          Define the core instructions that guide your agent's behavior.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowTemplates(!showTemplates)}
          className="rounded-xl"
        >
          <BookOpen className="mr-2 h-4 w-4" />
          Templates
          {showTemplates ? (
            <ChevronUp className="ml-2 h-4 w-4" />
          ) : (
            <ChevronDown className="ml-2 h-4 w-4" />
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleGeneratePrompt}
          className="rounded-xl"
        >
          <Wand2 className="mr-2 h-4 w-4" />
          Auto-Generate
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowVariables(!showVariables)}
          className="rounded-xl"
        >
          <Sparkles className="mr-2 h-4 w-4" />
          Variables
          {showVariables ? (
            <ChevronUp className="ml-2 h-4 w-4" />
          ) : (
            <ChevronDown className="ml-2 h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Templates Panel */}
      {showTemplates && (
        <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 space-y-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Choose a template to get started:
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            {PROMPT_TEMPLATES.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateSelect(template.prompt)}
                className="p-4 rounded-xl bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-600 text-left transition-all"
              >
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-purple-500" />
                  <p className="font-medium text-gray-900 dark:text-white text-sm">
                    {template.name}
                  </p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {template.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Variables Panel */}
      {showVariables && (
        <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 space-y-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Click to insert dynamic variables:
          </p>
          <div className="flex flex-wrap gap-2">
            {VARIABLE_SUGGESTIONS.map((variable) => (
              <button
                key={variable.name}
                onClick={() => insertVariable(variable.name)}
                className="px-3 py-1.5 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                title={variable.description}
              >
                {variable.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Prompt Editor */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="system-prompt" className="text-gray-700 dark:text-gray-300">
            System Prompt <span className="text-red-500">*</span>
          </Label>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleCopyPrompt} disabled={!state.systemPrompt}>
              <Copy className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleResetPrompt} disabled={!state.systemPrompt}>
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <Textarea
          id="system-prompt"
          value={state.systemPrompt}
          onChange={(e) => updateState({ systemPrompt: e.target.value })}
          placeholder="Enter the system prompt that defines your agent's behavior, personality, and capabilities..."
          className={`min-h-[300px] rounded-xl font-mono text-sm ${
            errors.systemPrompt ? 'border-red-500' : ''
          }`}
        />
        {errors.systemPrompt && (
          <div className="flex items-center gap-2 text-red-500 text-sm">
            <AlertCircle className="h-4 w-4" />
            {errors.systemPrompt}
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-3">
            <span>{wordCount} words</span>
            <span>{charCount} characters</span>
          </div>
          {state.useTemplate && (
            <Badge variant="secondary" className="text-xs">
              From template
            </Badge>
          )}
        </div>
      </div>

      {/* Tips */}
      <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              Tips for writing effective prompts:
            </p>
            <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-1 list-disc list-inside">
              <li>Be specific about the agent's role and capabilities</li>
              <li>Define clear guidelines and boundaries</li>
              <li>Include examples of expected behavior when helpful</li>
              <li>Specify the tone and style of communication</li>
              <li>List any restrictions or things the agent should avoid</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
